import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'
import { type Column } from 'grabbitbot-legacy-ui'
import { Table } from './DataTable'
import type { Billing, PaymentMethod, Transaction } from '../api/types'
import {
  TRANSACTION_CAUSE_LABELS,
  TRANSACTION_RESULT_LABELS,
  TRANSACTION_STATUS_LABELS,
  TRANSACTION_TYPE_LABELS,
} from '../lib/catalogue'
import { countryName } from '../lib/countries'
import { formatDateTime } from '../lib/format'
import { hasMethod, paymentMethodTone } from '../lib/paymentHelpers'

/** Card line of a saved payment method: `MASTERCARD ****4444`. Only brand,
 * last4 and expiry are ever read (no PAN/CVC exists on this side). */
export function PaymentMethodSummary({ pm }: { pm: PaymentMethod }) {
  const card = pm.primaryPaymentMethod?.card
  const brand = (card?.brand ?? pm.brand ?? '').toUpperCase()
  const last4 = card?.last4 ?? pm.last4 ?? ''
  return (
    <>
      <p className="fs-card-number">
        {brand} ****{last4}
      </p>
      {card && (
        <p className="portal-text-muted">
          Expires: {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
        </p>
      )}
      {Boolean(pm.expired) && <p className="portal-notice portal-notice--error">The payment method has been expired.</p>}
      {!pm.expired && Boolean(pm.expiresIn7Days) && (
        <p className="portal-notice portal-notice--warning">The payment method will expire in 7 days.</p>
      )}
      {!pm.expired && !pm.expiresIn7Days && Boolean(pm.expiresIn30Days) && (
        <p className="portal-notice portal-notice--warning">The payment method will expire in 30 days.</p>
      )}
    </>
  )
}

/** Port of `app-paymentmethod-info-block` (read-only summary + link). */
export function PaymentMethodInfoBlock({ projectId, pm }: { projectId: string; pm: PaymentMethod | null | undefined }) {
  if (pm === undefined || pm === null) return <div className="fs-panel">Loading payment method…</div>
  if (!hasMethod(pm)) {
    return (
      <div className="fs-panel fs-panel--warning">
        <h3>Payment method</h3>
        <p>{pm.createdAt ? 'Payment method removed.' : 'Payment method not set.'}</p>
      </div>
    )
  }
  return (
    <div className={`fs-panel ${paymentMethodTone(pm)}`}>
      <h3>Payment method</h3>
      <PaymentMethodSummary pm={pm} />
      <Link to={`/projects/${encodeURIComponent(projectId)}/payment/method`}>manage payment method</Link>
    </div>
  )
}

export function BillingDetails({ billing }: { billing: Billing }) {
  return (
    <dl className="portal-kv">
      <dt>Name</dt>
      <dd>{billing.name}</dd>
      <dt>Country</dt>
      <dd>{countryName(billing.country as string | null)}</dd>
      <dt>State</dt>
      <dd>{billing.state as string}</dd>
      <dt>City</dt>
      <dd>{billing.city as string}</dd>
      <dt>Postal code</dt>
      <dd>{(billing.postal_code ?? billing.postalCode) as string}</dd>
      <dt>Address line 1</dt>
      <dd>{billing.line1 as string}</dd>
      <dt>Address line 2</dt>
      <dd>{billing.line2 as string}</dd>
    </dl>
  )
}

/** Port of `app-billing-info-block`. */
export function BillingInfoBlock({ projectId, billing }: { projectId: string; billing: Billing | null | undefined }) {
  if (!billing) return <div className="fs-panel">Loading billing information…</div>
  return (
    <div className="fs-panel">
      <h3>Billing information</h3>
      <BillingDetails billing={billing} />
      <p className="portal-text-muted">
        {billing.line1
          ? 'If the current billing information is not correct, modify it before your next payment.'
          : 'If you need an invoice, please set up your billing information before payment.'}
      </p>
      <Link to={`/projects/${encodeURIComponent(projectId)}/payment/billing`}>manage billing</Link>
    </div>
  )
}

function transactionDate(t: Transaction): string {
  return formatDateTime(t.resultAt || t.createdAt)
}

function rowClass(t: Transaction): string | undefined {
  switch (Number(t.status)) {
    case 0:
      return 'fs-row--info'
    case 6:
      return 'fs-row--muted'
    case 9:
      return 'fs-row--danger-text'
    default:
      return undefined
  }
}

/** Port of the payment-transaction-list / admin-user-transaction-list table
 * (Date, Fee, Amount, Status, Source + an expandable details row). The
 * amount is the backend's own `amountFormatted` string, never recomputed. */
export function TransactionTable({ rows }: { rows: Transaction[] }) {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set())
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const columns: Column<Transaction>[] = [
    { key: 'date', label: 'Date', sortValue: (t) => t.resultAt || t.createdAt, format: (_v, t) => transactionDate(t) },
    { key: 'type', label: 'Fee', format: (v) => TRANSACTION_TYPE_LABELS[String(v)] ?? String(v ?? '') },
    {
      key: 'amount',
      label: 'Amount',
      align: 'right',
      numeric: true,
      format: (_v, t) => `${String(t.currency ?? '').toUpperCase()} ${t.amountFormatted ?? ''}`,
    },
    { key: 'status', label: 'Status', format: (v) => TRANSACTION_STATUS_LABELS[Number(v)] ?? String(v ?? '') },
    { key: 'source', label: 'Source', format: (v) => String(v || 'user') },
    {
      key: 'details',
      label: '',
      sortable: false,
      format: (_v, t) => (
        <button type="button" className="fs-link-button" aria-expanded={open.has(t.id)} onClick={() => toggle(t.id)}>
          {open.has(t.id) ? 'hide details' : 'details'}
        </button>
      ),
    },
  ]
  return (
    <div className="portal-table-scroll">
      <Table
        columns={columns}
        rows={rows}
        rowKey={(t) => t.id}
        rowClassName={rowClass}
        initialSort={{ key: 'date', dir: 'desc' }}
        renderExpanded={(t) =>
          open.has(t.id) ? (
            <tr className="portal-detail-row">
              <td colSpan={columns.length}>
                <TransactionDetails t={t} />
              </td>
            </tr>
          ) : null
        }
      />
    </div>
  )
}

function TransactionDetails({ t }: { t: Transaction }) {
  const details = (t.details && !Array.isArray(t.details) ? t.details : {}) as Record<string, unknown>
  const names = [...(t.planList ?? []), ...(t.productList ?? [])].map((x) => x.name)
  return (
    <dl className="portal-kv">
      {names.length > 0 && (
        <>
          <dt>{t.type === 'product' ? 'Product' : 'Subscription'}</dt>
          <dd>
            {names.map((n, i) => (
              <Fragment key={i}>
                {i > 0 && ', '}
                {n}
              </Fragment>
            ))}
          </dd>
        </>
      )}
      {t.type === 'custom' && (
        <>
          <dt>Custom payment</dt>
          <dd>{[t.customSource, t.customId, t.customName].filter(Boolean).join(' / ')}</dd>
        </>
      )}
      <dt>Cause</dt>
      <dd>{TRANSACTION_CAUSE_LABELS[String(t.cause)] ?? ''}</dd>
      <dt>Mode</dt>
      <dd>{t.mode === 'online' ? 'Started by user' : t.mode === 'offline' ? 'Automatic' : ''}</dd>
      <dt>Result</dt>
      <dd>{TRANSACTION_RESULT_LABELS[Number(t.result)] ?? ''}</dd>
      {typeof details.declineCode === 'string' && details.declineCode && (
        <>
          <dt>Decline code</dt>
          <dd>{details.declineCode}</dd>
        </>
      )}
      <dt>Initiated by</dt>
      <dd>{t.source || 'user'}</dd>
    </dl>
  )
}
